import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { getTodayData, getConversations, seedData, createConversation, createMessage, updateRequestStatus, createEscalation, startShift, getActiveShift, updateTaskStatus as apiUpdateTaskStatus, markWelcomeSeen } from '../../lib/api';
import { useProperty } from '../../context/PropertyContext';
import { PropertyBadge } from '../../components/PropertyBadge';
import { SignalLine, PreventRepeatModal } from '../../components/ExperienceProtection';
import { LogRequestModal } from '../../components/LogRequestModal';
import { TaskCard } from '../../components/TaskCard';
import { FinancialImpactCard } from '../../components/FinancialImpactCard';
import { buildTaskTitle } from '../../lib/taskTitles';
import { useAuth } from '../../context/AuthContext';
import { categorizeMessage, calculateSLATimer, CATEGORY_TAGS, ESCALATION_REASONS, ESCALATION_ASSIGN_OPTIONS, GUEST_UPDATE_SCRIPTS } from '../../lib/workQueue';
import { useGuestHubArrivalToast } from '../../hooks/useGuestHubArrivalToast';
import { 
  AlertTriangle, Clock, Star, Bell, MessageSquare, CheckCircle2, Plus, Database, Loader2, 
  ChevronDown, ChevronRight, Target, ArrowRight, Check, Inbox, Play, Pause,
  HelpCircle, DollarSign, Flag, Timer, Sun, Coffee, Calendar, CalendarClock, CreditCard, Home, MoreHorizontal, RefreshCw,
  FileText, Sparkles, X
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

// Issue label types
const ISSUE_LABELS = {
  ISSUE: { label: 'Issue', color: 'bg-red-100 text-red-700' },
  BOOKING: { label: 'Booking', color: 'bg-blue-100 text-blue-700' },
  ARRIVAL: { label: 'Arrival', color: 'bg-amber-100 text-amber-700' },
  PAYMENT: { label: 'Payment', color: 'bg-purple-100 text-purple-700' },
  OTHER: { label: 'Other', color: 'bg-stone-100 text-stone-700' }
};

// Get issue label from subject/message
const getIssueLabel = (subject = '', message = '') => {
  const text = (subject + ' ' + message).toLowerCase();
  if (text.includes('check-in') || text.includes('checking in') || text.includes('arrival') || text.includes('room not ready')) return 'ARRIVAL';
  if (text.includes('book') || text.includes('inquiry') || text.includes('availability')) return 'BOOKING';
  if (text.includes('payment') || text.includes('refund') || text.includes('charge') || text.includes('bill')) return 'PAYMENT';
  if (text.includes('hot water') || text.includes('wifi') || text.includes('broken') || text.includes('not working') || text.includes('issue')) return 'ISSUE';
  return 'OTHER';
};

// Get summary line from message
const getSummaryLine = (subject = '', message = '') => {
  const text = (message || subject).toLowerCase();
  if (text.includes('hot water')) return 'No hot water reported';
  if (text.includes('room') && text.includes('ready')) return 'Room not ready yet';
  if (text.includes('check-in') || text.includes('checking in')) return 'Check-in assistance needed';
  if (text.includes('wifi') || text.includes('wi-fi')) return 'Wi-Fi connectivity issue';
  if (text.includes('late checkout')) return 'Late checkout request';
  if (text.includes('transfer')) return 'Transfer arrangement needed';
  if (text.includes('availability') || text.includes('inquiry')) return 'Availability inquiry';
  return subject || 'Guest needs assistance';
};

export const Today = () => {
  const navigate = useNavigate();
  const { properties, selectedPropertyId, getPropertyName, refreshProperties } = useProperty();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [showNewConv, setShowNewConv] = useState(false);
  const [calmMode, setCalmMode] = useState(false);
  // First-login welcome card. Source of truth is the server flag
  // (`hasSeenWelcome`); local state is just an optimistic-dismiss layer so
  // the card disappears instantly when the user clicks "Got it".
  const [welcomeDismissedLocal, setWelcomeDismissedLocal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    urgent: true,
    dueSoon: true,
    followups: true,
    reviews: true,
    requests: true
  });
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateItem, setEscalateItem] = useState(null);
  const [escalateData, setEscalateData] = useState({
    reason: '',
    assignTo: '',
    internalNotes: ''
  });
  const [newConvData, setNewConvData] = useState({
    propertyId: '',
    guestName: '',
    subject: '',
    channel: 'EMAIL',
    initialMessage: ''
  });
  const [preventRepeatOpen, setPreventRepeatOpen] = useState(false);
  const [preventRepeatTask, setPreventRepeatTask] = useState(null);
  const [logRequestOpen, setLogRequestOpen] = useState(false);
  const [shiftStarted, setShiftStarted] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedPropertyId]);

  // Poll for new GuestHub arrivals → show toast when fresh requests land.
  useGuestHubArrivalToast({ propertyId: selectedPropertyId });

  // Tue/Fri: nudge operator to check reviews (in-app toast, once per day)
  useEffect(() => {
    try {
      const now = new Date();
      const day = now.getDay(); // 0=Sun ... 2=Tue, 5=Fri
      if (day !== 2 && day !== 5) return;
      const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const flag = `maris_review_nudge_${dateKey}`;
      if (localStorage.getItem(flag)) return;
      const t = setTimeout(() => {
        toast.info('Reviews check-in', {
          description: 'It\u2019s Tuesday/Friday — take 2 minutes to reply to pending reviews.',
          duration: 7000,
          action: { label: 'Open Reviews', onClick: () => navigate('/desk/reviews') },
        });
        localStorage.setItem(flag, '1');
      }, 1200);
      return () => clearTimeout(t);
    } catch {}
  }, [navigate]);

  // Show the welcome card while the server-side flag is still false AND the
  // user hasn't dismissed it in this session. Hides cleanly for returning
  // users without flicker because we read from the auth context, which is
  // hydrated synchronously from localStorage on first paint.
  const showWelcomeCard = !!authUser && authUser.hasSeenWelcome === false && !welcomeDismissedLocal;

  const handleDismissWelcome = async () => {
    setWelcomeDismissedLocal(true);
    try {
      await markWelcomeSeen();
      // Keep localStorage in sync so a hard refresh doesn't replay the card
      // until /auth/me hydrates.
      const stored = JSON.parse(localStorage.getItem('maris_user') || '{}');
      stored.hasSeenWelcome = true;
      localStorage.setItem('maris_user', JSON.stringify(stored));
    } catch (err) {
      // Non-blocking — the card stays dismissed locally even if the
      // server call fails; it'll retry on next dismiss/login.
      console.warn('Could not persist welcome dismissal', err?.response?.status);
    }
  };

  const loadData = async () => {
    try {
      const [todayRes, convsRes, shiftRes] = await Promise.all([
        getTodayData(),
        getConversations(selectedPropertyId !== 'all' ? { propertyId: selectedPropertyId } : {}),
        getActiveShift(),
      ]);
      setData(todayRes.data);
      setConversations(convsRes.data);
      setShiftStarted(!!shiftRes.data?.active_shift);
    } catch (error) {
      console.error('Failed to load today data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedData();
      await refreshProperties();
      await loadData();
      toast.success('Showcase workspace loaded.');
    } catch (error) {
      if (error.response?.data?.message === 'Already seeded') {
        toast.info('Showcase workspace already exists');
        await loadData();
      } else {
        toast.error('Could not load showcase workspace');
      }
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!newConvData.propertyId || !newConvData.guestName || !newConvData.subject) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const convRes = await createConversation({
        propertyId: newConvData.propertyId,
        guestName: newConvData.guestName,
        subject: newConvData.subject,
        channel: newConvData.channel
      });
      
      if (newConvData.initialMessage) {
        await createMessage({
          conversationId: convRes.data.id,
          direction: 'INBOUND',
          bodyText: newConvData.initialMessage,
          category: 'INQUIRY',
          priority: 'NORMAL'
        });
      }
      
      setShowNewConv(false);
      setNewConvData({ propertyId: '', guestName: '', subject: '', channel: 'EMAIL', initialMessage: '' });
      await loadData();
      toast.success('Conversation created');
      navigate(`/desk/inbox?conversation=${convRes.data.id}`);
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };

  const handleRequestAction = async (requestId, action) => {
    try {
      if (action === 'IN_PROGRESS' || action === 'DONE') {
        await updateRequestStatus(requestId, action);
        await loadData();
        toast.success('Marked done.');
      }
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleEscalate = (item, convId) => {
    setEscalateItem({ ...item, conversationId: convId });
    setEscalateData({ reason: '', assignTo: '', internalNotes: '' });
    setShowEscalateModal(true);
  };

  const handleCreateEscalation = async () => {
    if (!escalateData.reason || !escalateData.assignTo) {
      toast.error('Please select reason and assignee');
      return;
    }
    
    try {
      const res = await createEscalation({
        conversationId: escalateItem.conversationId,
        reason: escalateData.reason,
        assignTo: escalateData.assignTo,
        internalNotes: escalateData.internalNotes
      });
      
      setShowEscalateModal(false);
      toast.success('Escalation created. Guest update is ready to send.');
      navigate(`/desk/inbox?conversation=${escalateItem.conversationId}&prefill=${encodeURIComponent(res.data.guestUpdateScript)}`);
    } catch (error) {
      toast.error('Failed to create escalation');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Calculate work queue items
  const urgentItems = data?.urgent || [];
  const dueSoonItems = data?.dueSoon || [];
  const followupItems = data?.followupsDue || [];
  const reviewItems = data?.reviewsPending || [];
  const requestItems = data?.recentRequests || [];

  // Map impact_area -> Task.category enum for title dictionary lookup
  const impactToCategory = {
    access: 'ACCESS',
    comfort: 'MAINTENANCE',
    trust: 'IN_STAY_ISSUE',
    revenue: 'BOOKING',
    safety: 'MAINTENANCE',
  };

  // Map RequestType enum -> Task.category enum for title dictionary lookup
  const requestTypeToCategory = {
    LATE_CHECKOUT: 'ARRIVAL',
    EARLY_CHECKIN: 'ARRIVAL',
    TRANSFER: 'TRANSPORT',
    AIRPORT_PICKUP: 'TRANSPORT',
    AIRPORT_DROPOFF: 'TRANSPORT',
    TOUR_BOOKING: 'TRANSPORT',
    CAR_RENTAL: 'TRANSPORT',
    EXTRA_TOWELS: 'HOUSEKEEPING',
    LINEN_CHANGE: 'HOUSEKEEPING',
    MID_STAY_CLEAN: 'HOUSEKEEPING',
    HOUSEKEEPING: 'HOUSEKEEPING',
    MAINTENANCE: 'MAINTENANCE',
    NOISE: 'MAINTENANCE',
    COMFORT_ISSUE: 'IN_STAY_ISSUE',
    SPECIAL_REQUEST: 'IN_STAY_ISSUE',
    BOOKING: 'BOOKING',
    ACCESS: 'ACCESS',
  };

  // Humanise requestType for SUBTYPE regex matching (e.g. LATE_CHECKOUT → 'late checkout request')
  const requestTypeLabel = {
    LATE_CHECKOUT: 'late checkout request',
    EARLY_CHECKIN: 'early check-in',
    TRANSFER: 'airport pickup',
    AIRPORT_PICKUP: 'airport pickup',
    AIRPORT_DROPOFF: 'airport drop-off',
    TOUR_BOOKING: 'tour booking',
    CAR_RENTAL: 'car rental',
    EXTRA_TOWELS: 'extra towels',
    LINEN_CHANGE: 'linen change',
    MID_STAY_CLEAN: 'mid-stay clean',
    NOISE: 'noise complaint',
    COMFORT_ISSUE: 'comfort issue',
    SPECIAL_REQUEST: 'special request',
  };

  // Map a Priority Inbox / Due Soon item to TaskCard shape
  const toTaskCardItem = (item, urgent = false) => {
    // Prefer the linked Task's category enum (set by backend enrichment) over message-level category
    const ia = String(item.impact_area || '').toLowerCase();
    const derivedCategory = item.taskCategory || item.category || impactToCategory[ia] || null;
    // Prefer taskSummary (concise) → subject → bodyText for dictionary regex matching
    const titleSource = item.taskSummary
      || [item.subject, item.bodyText].filter(Boolean).join(' — ')
      || getSummaryLine(item.subject, item.bodyText);
    return {
      id: item.id || item.conversationId,
      summary: titleSource,
      category: derivedCategory,
      propertyId: item.propertyId,
      guest: item.guestName,
      channel: item.channel,
      source_channel: item.channel,
      last_updated_at: item.createdAt,
      priority: urgent ? 'HANDLE_NOW' : 'DUE_TODAY',
      status: 'New',
      dueAt: item.dueAt || null,
      nextStep: urgent ? 'Reply within 30 min — guest is waiting.' : 'Reply by end of day.',
      owner: item.assignedTo || null,
      route_to: item.route_to || null,
      signal_type: item.signal_type,
      impact_area: item.impact_area,
      fix_route: item.fix_route,
      prevent_repeat_note: item.prevent_repeat_note,
      prevent_repeat_action: item.prevent_repeat_action,
      conversationId: item.conversationId || item.id,
      _raw: item,
    };
  };

  const totalWorkItems = urgentItems.length + dueSoonItems.length + followupItems.length + 
                         reviewItems.length + requestItems.length;
  const isEmpty = totalWorkItems === 0 && conversations.length === 0;
  const isAllClear = totalWorkItems === 0 && conversations.length > 0;

  // Calculate estimated time for next best action
  const urgentCount = urgentItems.length;
  const estMinutes = urgentCount * 3; // ~3 min per message

  // In calm mode, only show urgent, due soon, and followups due today
  const showSection = (section) => {
    if (!calmMode) return true;
    return ['urgent', 'dueSoon', 'followups'].includes(section);
  };

  // Priority Inbox Item Component
  const PriorityInboxItem = ({ item, convId }) => {
    const issueLabel = getIssueLabel(item.subject, item.bodyText);
    const labelStyle = ISSUE_LABELS[issueLabel];
    const summaryLine = getSummaryLine(item.subject, item.bodyText);
    const sla = calculateSLATimer(item.createdAt);

    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-stone-100 hover:border-stone-200 transition-colors group" data-testid="priority-inbox-item">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${labelStyle.color}`}>
                {labelStyle.label}
              </Badge>
              <span className="text-sm font-medium text-stone-900">{item.guestName || 'Guest'}</span>
            </div>
            <p className="text-sm text-stone-600">{summaryLine}</p>
            <SignalLine signal_type={item.signal_type} impact_area={item.impact_area} fix_route={item.fix_route} />
            <div className="flex items-center gap-2">
              {item.propertyId && (
                <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />
              )}
              {sla && (
                <span className="text-xs text-stone-400">• {sla.text.replace('Waiting ', '')} ago</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-3">
          <Button
            size="sm"
            variant="ghost"
            className="text-xs h-7 px-2 text-stone-500 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
            data-testid="prevent-repeat-button"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Prevent repeat
          </Button>
          <Button
            size="sm"
            className="bg-[#3F617E] hover:bg-[#345470] rounded-full text-xs h-8 px-4"
            onClick={() => navigate(`/desk/inbox?conversation=${convId || item.conversationId}`)}
          >
            Handle now
          </Button>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => toast.success('Snoozed for 1 hour')}
                >
                  <Pause className="h-3.5 w-3.5 text-stone-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Snooze</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    );
  };

  // Guest Request Item Component
  const GuestRequestItem = ({ item }) => {
    const statusColors = {
      NEW: 'bg-blue-100 text-blue-700',
      IN_PROGRESS: 'bg-amber-100 text-amber-700',
      DONE: 'bg-green-100 text-green-700'
    };
    const statusLabels = {
      NEW: 'New',
      IN_PROGRESS: 'In progress',
      DONE: 'Closed out'
    };

    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-stone-100 hover:border-stone-200 transition-colors">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <p className="text-sm font-semibold text-stone-900 leading-snug line-clamp-1">
              {buildTaskTitle({
                category: item.category || requestTypeToCategory[item.requestType] || impactToCategory[item.impact_area],
                summary: [item.subject, item.details, item.summary, requestTypeLabel[item.requestType] || item.requestType].filter(Boolean).join(' — '),
                guest_name: item.guestName,
              })}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {item.propertyId && (
                <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />
              )}
              <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${statusColors[item.status] || statusColors.NEW}`}>
                {statusLabels[item.status] || 'New'}
              </Badge>
              {item.guestName ? null : <span className="text-xs text-stone-400 italic">Guest: Not linked</span>}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-3">
          {item.status === 'NEW' && (
            <Button
              size="sm"
              className="bg-[#3F617E] hover:bg-[#345470] rounded-full text-xs h-8"
              onClick={() => handleRequestAction(item.id, 'IN_PROGRESS')}
              data-testid="take-this-btn"
            >
              <Play className="h-3 w-3 mr-1" />
              Take this
            </Button>
          )}
          {item.status === 'IN_PROGRESS' && (
            <Button
              size="sm"
              className="bg-[#3F617E] hover:bg-[#345470] rounded-full text-xs h-8"
              onClick={() => handleRequestAction(item.id, 'DONE')}
              data-testid="close-out-btn"
            >
              <Check className="h-3 w-3 mr-1" />
              Close out
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Follow-up Item — Today's Follow-ups section card
  const FOLLOWUP_OWNER_LABELS = {
    front_desk: 'Front Desk', housekeeping: 'Housekeeping', maintenance: 'Maintenance',
    transport: 'Transport / Driver', concierge: 'Concierge', reservations: 'Reservations',
    manager_on_duty: 'Manager on Duty',
  };
  const prettyOwner = (raw) => raw ? (FOLLOWUP_OWNER_LABELS[String(raw).toLowerCase()] || String(raw)) : '';
  const FollowupItem = ({ item, onOpen }) => {
    const title = buildTaskTitle({
      category: item.category || item.taskCategory,
      summary: item.summary || item.subject || item.taskSummary,
      guest_name: item.guestName || item.guest_name_text,
    });
    const nextTouch = item.next_touch_at ? new Date(item.next_touch_at) : null;
    const nextTouchLabel = nextTouch
      ? nextTouch.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : '';
    const owner = prettyOwner(item.assigned_to_user_id || item.assignedTo) || 'Unassigned';
    const routeTo = prettyOwner(item.route_to);
    return (
      <div
        className="rounded-lg border border-stone-100 bg-white hover:border-stone-200 transition-colors p-3"
        data-testid={`followup-item-${item.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-stone-900 leading-snug line-clamp-1">{title}</p>
            <div className="flex items-center gap-2 flex-wrap mt-1 text-xs text-stone-500">
              {item.propertyId && <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />}
              {nextTouchLabel && (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <CalendarClock className="h-3 w-3" /> Next touch: Today {nextTouchLabel}
                </span>
              )}
            </div>
            {item.next_touch_reason && (
              <p className="text-xs text-stone-600 mt-1 line-clamp-2">
                <span className="text-stone-500">Reason:</span> {item.next_touch_reason}
              </p>
            )}
            <div className="flex items-center gap-x-3 gap-y-1 mt-1 text-xs text-stone-500 flex-wrap">
              <span><span className="font-medium text-stone-600">Owner:</span> {owner}</span>
              {routeTo && (
                <span>
                  <span className="font-medium text-stone-600">Route to:</span>{' '}
                  <span className="inline-flex items-center gap-0.5 text-[#3F617E]">{routeTo}</span>
                </span>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-8 rounded-full text-xs flex-shrink-0"
            onClick={onOpen}
            data-testid={`followup-open-${item.id}`}
          >
            Open
          </Button>
        </div>
      </div>
    );
  };

  // Summary Card Component
  const SummaryCard = ({ title, count, icon: Icon, color, helper, emptyText, onClick }) => (    <Card 
      className="border-stone-200 hover:border-stone-300 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="text-2xl font-heading font-medium">{count}</span>
        </div>
        <p className="text-sm font-medium text-stone-900">{title}</p>
        <p className="text-xs text-stone-500 mt-0.5">
          {count === 0 ? emptyText : helper}
        </p>
      </CardContent>
    </Card>
  );

  // Empty State Component
  const EmptyState = () => (
    <Card className="border-stone-200 border-dashed bg-stone-50/50">
      <CardContent className="p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
          <Database className="h-8 w-8 text-stone-400" />
        </div>
        <h3 className="font-heading text-xl text-stone-900 mb-2">Nothing to review yet</h3>
        <p className="text-stone-500 mb-6 max-w-sm mx-auto">
          As guest messages come in or tasks are added to your Daily Run, they'll appear here.
        </p>
        <div className="flex justify-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowNewConv(true)}
            className="rounded-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Conversation
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // All Clear State Component
  const AllClearState = () => (
    <Card className="border-green-200 bg-green-50/50">
      <CardContent className="p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Sun className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="font-heading text-xl text-stone-900 mb-2">You&apos;re clear for now.</h3>
        <p className="text-stone-500 mb-6 max-w-sm mx-auto">
          No urgent messages or follow-ups are waiting. You can check back later.
        </p>
        <div className="flex justify-center gap-3">
          <Button 
            variant="outline"
            onClick={() => navigate('/desk/inbox')}
            className="rounded-full"
          >
            <Inbox className="h-4 w-4 mr-2" />
            Go to Inbox
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowNewConv(true)}
            className="rounded-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create new conversation
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Escalate Modal
  const EscalateModal = () => (
    <Dialog open={showEscalateModal} onOpenChange={setShowEscalateModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Escalate this issue</DialogTitle>
          <DialogDescription>
            Create a clear task and send a calm update to the guest.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Reason</Label>
            <Select value={escalateData.reason} onValueChange={(v) => setEscalateData({...escalateData, reason: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {ESCALATION_REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Assign to</Label>
            <Select value={escalateData.assignTo} onValueChange={(v) => setEscalateData({...escalateData, assignTo: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                {ESCALATION_ASSIGN_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Internal notes</Label>
            <Textarea 
              value={escalateData.internalNotes} 
              onChange={(e) => setEscalateData({...escalateData, internalNotes: e.target.value})}
              placeholder="Add any context for the team..."
              rows={3}
            />
          </div>
          <div className="pt-2 space-y-3">
            <div className="text-xs text-stone-500">
              Guest update will be added to composer:
            </div>
            <div className="p-3 bg-stone-50 rounded-lg text-sm text-stone-600 italic">
              &ldquo;{GUEST_UPDATE_SCRIPTS.default}&rdquo;
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline"
              onClick={() => setShowEscalateModal(false)} 
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEscalation} 
              className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
            >
              Create task
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // New Conversation Dialog
  const NewConversationDialog = () => (
    <Dialog open={showNewConv} onOpenChange={setShowNewConv}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">New Conversation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select value={newConvData.propertyId} onValueChange={(v) => setNewConvData({...newConvData, propertyId: v})}>
              <SelectTrigger data-testid="new-conv-property-select">
                <SelectValue placeholder={properties.length === 0 ? "No properties added yet" : "Select property"} />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Guest Name *</Label>
            <Input 
              value={newConvData.guestName} 
              onChange={(e) => setNewConvData({...newConvData, guestName: e.target.value})}
              placeholder="Guest name"
              data-testid="new-conv-guest-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input 
              value={newConvData.subject} 
              onChange={(e) => setNewConvData({...newConvData, subject: e.target.value})}
              placeholder="e.g., Inquiry: Jan 10-14"
              data-testid="new-conv-subject"
            />
          </div>
          <div className="space-y-2">
            <Label>Channel</Label>
            <Select value={newConvData.channel} onValueChange={(v) => setNewConvData({...newConvData, channel: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EMAIL">Email</SelectItem>
                <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                <SelectItem value="IG">Instagram</SelectItem>
                <SelectItem value="OTA">OTA</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Initial Message (paste inbound)</Label>
            <Textarea 
              value={newConvData.initialMessage} 
              onChange={(e) => setNewConvData({...newConvData, initialMessage: e.target.value})}
              placeholder="Paste the guest's message here..."
              rows={4}
              data-testid="new-conv-message"
            />
          </div>
          <Button 
            onClick={handleCreateConversation} 
            className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
            data-testid="create-conversation-button"
          >
            Create Conversation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64" />
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="today-dashboard">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Today</h1>
            <p className="text-sm text-stone-500 mt-1">
              Everything that needs attention — sorted by priority.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="calm-mode"
                checked={calmMode}
                onCheckedChange={setCalmMode}
              />
              <Label htmlFor="calm-mode" className="text-sm text-stone-600 cursor-pointer">
                Calm Mode
              </Label>
            </div>
            <Button 
              onClick={() => setLogRequestOpen(true)}
              variant="outline"
              className="rounded-full"
              data-testid="today-log-request-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Log request
            </Button>
            {!shiftStarted ? (
              <Button
                onClick={async () => {
                  try { await startShift(); setShiftStarted(true); toast.success('Shift started.'); } catch {}
                }}
                className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                data-testid="today-start-shift-btn"
              >
                <Play className="h-4 w-4 mr-2" /> Start shift
              </Button>
            ) : (
              <Badge className="bg-green-100 text-green-700 px-3 py-1">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Shift Active
              </Badge>
            )}
          </div>
        </div>

        {/* First-login welcome card — dismissible, gated by server flag */}
        {showWelcomeCard && (
          <div
            className="relative overflow-hidden rounded-2xl border border-[#3F617E]/15 bg-gradient-to-br from-[#3F617E]/[0.06] via-white to-amber-50/40 p-5 sm:p-6 animate-fade-in"
            data-testid="welcome-card"
          >
            <button
              type="button"
              onClick={handleDismissWelcome}
              className="absolute top-3 right-3 p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"
              aria-label="Dismiss welcome message"
              data-testid="welcome-card-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-[#3F617E] text-white grid place-items-center">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-heading font-medium text-stone-900">
                  Welcome to Maris Desk{authUser?.name ? `, ${authUser.name.split(' ')[0]}` : ''}
                </h2>
                <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">
                  This is <span className="font-medium text-stone-800">Today</span> — every guest message,
                  follow-up, and task ready for action, sorted by urgency. Start your shift to see SLA
                  timers tick live, or log a request manually if a guest reached out off-channel.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleDismissWelcome}
                    className="bg-[#3F617E] hover:bg-[#345470] rounded-full"
                    data-testid="welcome-card-ack"
                  >
                    Got it
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate('/desk/inbox')}
                    className="rounded-full text-stone-600 hover:text-stone-900"
                  >
                    Open Inbox <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Next Best Action - Signature Detail */}
        {!isEmpty && urgentCount > 0 && !calmMode && (
          <div className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl border border-stone-100">
            <Target className="h-5 w-5 text-[#3F617E]" />
            <p className="text-sm text-stone-700">
              <span className="font-medium">Next best action:</span> Reply to {urgentCount} urgent guest{urgentCount !== 1 ? 's' : ''} (~{estMinutes} min)
            </p>
            <ArrowRight className="h-4 w-4 text-stone-400 ml-auto" />
          </div>
        )}

        {/* Calm Mode Banner */}
        {calmMode && (
          <div className="flex items-center justify-between p-3 bg-stone-100 rounded-lg text-sm text-stone-600">
            <div className="flex items-center gap-2">
              <Coffee className="h-4 w-4" />
              <span>Calm Mode is on. Only the essentials are showing.</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setCalmMode(false)}
              className="text-stone-600 hover:text-stone-900"
            >
              Turn off
            </Button>
          </div>
        )}

        <NewConversationDialog />
        <EscalateModal />
        <LogRequestModal
          open={logRequestOpen}
          onOpenChange={setLogRequestOpen}
          onCreated={() => loadData()}
          defaultPropertyId={selectedPropertyId !== 'all' ? selectedPropertyId : ''}
        />
        {preventRepeatTask && (
          <PreventRepeatModal
            open={preventRepeatOpen}
            onOpenChange={setPreventRepeatOpen}
            taskId={preventRepeatTask.taskId || preventRepeatTask.id || preventRepeatTask.conversationId}
            existingNote={preventRepeatTask.prevent_repeat_note}
            existingAction={preventRepeatTask.prevent_repeat_action}
            onSaved={() => loadData()}
          />
        )}

        {isEmpty ? (
          <EmptyState />
        ) : isAllClear ? (
          <AllClearState />
        ) : (
          <>
            {/* Summary Cards */}
            {!calmMode && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard
                  title="Urgent"
                  count={urgentItems.length}
                  icon={AlertTriangle}
                  color="bg-red-100 text-red-600"
                  helper="Reply today"
                  emptyText="No urgent items"
                  onClick={() => navigate('/desk/inbox?priority=URGENT')}
                />
                <SummaryCard
                  title="Due Today"
                  count={dueSoonItems.length}
                  icon={Clock}
                  color="bg-amber-100 text-amber-600"
                  helper="Complete before end of day"
                  emptyText="Nothing due today"
                  onClick={() => navigate('/desk/daily-run')}
                />
                <SummaryCard
                  title="Follow-ups"
                  count={followupItems.length}
                  icon={Bell}
                  color="bg-blue-100 text-blue-600"
                  helper="Awaiting your update"
                  emptyText="No follow-ups right now"
                  onClick={() => navigate('/desk/followups')}
                />
                <SummaryCard
                  title="Reports"
                  count=""
                  icon={FileText}
                  color="bg-stone-100 text-stone-700"
                  helper="Concierge briefings + audit"
                  emptyText="Concierge briefings + audit"
                  onClick={() => navigate('/desk/reports')}
                />
              </div>
            )}

            {/* Financial Impact widget — calm value summary for owners/GMs */}
            {!calmMode && (
              <FinancialImpactCard />
            )}

            {/* Priority Inbox */}
            {urgentItems.length > 0 && (
              <Card className="border-stone-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">Handle Now</CardTitle>
                      <p className="text-xs text-stone-500 mt-0.5">Time-sensitive. Protects arrival, access, comfort, or trust.</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-[#3F617E]" onClick={() => navigate('/desk/inbox?priority=URGENT')}>
                      View all
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {urgentItems.slice(0, 5).map((item, idx) => {
                    const tcItem = toTaskCardItem(item, true);
                    return (
                      <TaskCard
                        key={item.id || idx}
                        item={tcItem}
                        variant="handle_now"
                        onHandle={() => navigate(`/desk/inbox?conversation=${tcItem.conversationId}`)}
                        onPreventRepeat={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
                      />
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Priority inbox empty state */}
            {urgentItems.length === 0 && !calmMode && (
              <Card className="border-stone-200 border-dashed">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
                  <p className="text-stone-600 font-medium">All clear — nothing urgent right now.</p>
                </CardContent>
              </Card>
            )}

            {/* Due Today Section */}
            {showSection('dueSoon') && dueSoonItems.length > 0 && (
              <Card className="border-stone-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">Due Today</CardTitle>
                      <p className="text-xs text-stone-500 mt-0.5">Items that should be completed before end of day.</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-[#3F617E]" onClick={() => navigate('/desk/daily-run')}>
                      View all
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {dueSoonItems.slice(0, 3).map((item, idx) => {
                    const tcItem = toTaskCardItem(item, false);
                    // Items sourced from open DUE_TODAY tasks (added via Daily Run)
                    // have a taskId but their conversation is already on the run —
                    // send the user to Daily Run, not back into Inbox.
                    const handleNav = item.taskId
                      ? () => navigate('/desk/daily-run')
                      : () => navigate(`/desk/inbox?conversation=${tcItem.conversationId}`);
                    return (
                      <TaskCard
                        key={item.id || idx}
                        item={tcItem}
                        variant="due_today"
                        onHandle={handleNav}
                        onPreventRepeat={() => { setPreventRepeatTask(item); setPreventRepeatOpen(true); }}
                      />
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Follow-ups Section — tasks with next_touch_at scheduled for today */}
            {showSection('followups') && followupItems.length > 0 && (
              <Card className="border-stone-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">Follow-ups</CardTitle>
                      <p className="text-xs text-stone-500 mt-0.5">Active tasks scheduled to come back to today.</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-[#3F617E]" onClick={() => navigate('/desk/daily-run')}>
                      View all
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {followupItems.slice(0, 5).map((item, idx) => (
                    <FollowupItem key={item.id || idx} item={item} onOpen={() => navigate('/desk/daily-run')} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Requests Queue - Only show when not in calm mode */}
            {!calmMode && requestItems.length > 0 && (
              <Card className="border-stone-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">Requests queue</CardTitle>
                      <p className="text-xs text-stone-500 mt-0.5">Guest requests you've taken on. Close out as each one wraps.</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {requestItems.slice(0, 5).map((item, idx) => (
                    <GuestRequestItem key={item.id || idx} item={item} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Requests queue empty state */}
            {!calmMode && requestItems.length === 0 && (
              <Card className="border-stone-200 border-dashed">
                <CardContent className="p-6 text-center">
                  <p className="text-stone-500 text-sm">No guest requests right now.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DeskLayout>
  );
};

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function getFirstName() {
  const user = JSON.parse(localStorage.getItem('maris_user') || '{}');
  return user.name?.split(' ')[0] || 'there';
}

export default Today;
